<template>
  <div class="bg-gray-50 rounded-xl shadow-lg overflow-hidden hide-scrollbar pt-2">
    <!-- Loading state -->
    <div v-if="isLoading" class="p-8 flex flex-col items-center justify-center">
      <svg class="animate-spin h-10 w-10 text-teal-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-gray-600">Loading train information...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="p-8 text-center">
      <div class="mb-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <p class="text-lg font-medium text-gray-800 mb-2">No trains found</p>
        <p class="text-gray-600 text-sm">Please try different stations or check your search</p>
      </div>
      <button @click="$emit('retry')" class="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
        Try Another Search
      </button>
    </div>

    <!-- No results state -->
    <div v-else-if="trains.length === 0" class="p-8 text-center">
      <div class="mb-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 20a7.962 7.962 0 01-5-1.709M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <p class="text-lg font-medium text-gray-800 mb-2">No trains found</p>
        <p class="text-gray-600 text-sm">No trains available for this route</p>
      </div>
      <button @click="$emit('retry')" class="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
        Try Another Search
      </button>
    </div>

    <!-- Results list -->
    <div v-else class="p-4 pb-6">
      <!-- Search summary -->
      <div class="bg-teal-500 text-white rounded-lg mb-4">
        <div class="p-4">
          <div class="flex items-center justify-between">
            <div>
              <div class="text-sm opacity-80">From</div>
              <div class="font-bold">{{ from }}</div>
            </div>

            <div class="flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>

            <div>
              <div class="text-sm opacity-80">To</div>
              <div class="font-bold">{{ to }}</div>
            </div>

          </div>
        </div>
      </div>

      <p class="text-gray-600 mb-4">{{ filteredTrains.length }} trains found</p>

      <div class="space-y-4 mb-2">
        <div v-for="(train, index) in filteredTrains" :key="index" class="bg-white rounded-xl shadow-sm overflow-hidden mb-2">
          <!-- Train header -->
          <div class="bg-gray-50 p-4 border-b">
            <div class="flex justify-between items-center">
              <div>
                <span class="bg-teal-100 text-teal-800 text-xs font-medium px-2 py-1 rounded">{{ train.number }}</span>
                <span class="ml-2 font-bold">{{ train.name }}</span>
              </div>
              <div class="text-sm text-gray-500">
                {{ train.runs_on }}
              </div>
            </div>
          </div>

          <!-- Train details -->
          <div class="p-4">
            <div class="flex justify-between items-start">
              <!-- Departure -->
              <div class="flex-1">
                <div class="text-xl font-bold">{{ train.departure.time }}</div>
                <div class="font-medium">{{ train.departure.station_name }}</div>
                <div class="text-sm text-gray-500">{{ train.departure.station_code }}</div>
              </div>

              <!-- Journey info -->
              <div class="flex-1 text-center">
                <div class="text-sm text-gray-500 mb-1">{{ train.duration }}</div>
                <div class="relative">
                  <div class="h-0.5 bg-gray-300 w-full absolute top-1/2 transform -translate-y-1/2"></div>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mx-auto relative" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="text-sm text-gray-500 mt-1">{{ train.distance }}</div>
              </div>

              <!-- Arrival -->
              <div class="flex-1 text-right">
                <div class="text-xl font-bold">{{ train.arrival.time }}</div>
                <div class="font-medium">{{ train.arrival.station_name }}</div>
                <div class="text-sm text-gray-500">{{ train.arrival.station_code }}</div>
              </div>
            </div>

            <!-- Additional info -->
            <div class="mt-4 pt-4 border-t flex justify-between text-sm text-gray-500">
              <div>Speed: {{ train.speed }}</div>
              <button class="text-teal-500 font-medium">View Details</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrainResultsComponent',
  props: {
    from: {
      type: String,
      required: true
    },
    to: {
      type: String,
      required: true
    },
    trains: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  computed: {
    filteredTrains() {
      const now = new Date()
      const currentHours = now.getHours()
      const currentMinutes = now.getMinutes()
      const currentTimeInMinutes = currentHours * 60 + currentMinutes

      return this.trains.filter(train => {
        const [departureHours, departureMinutes] = train.departure.time.split(':').map(Number)
        const departureTimeInMinutes = departureHours * 60 + departureMinutes

        // For trains departing after midnight (00:00 to 05:59), add 24 hours to make them appear after current day's trains
        const adjustedDepartureTime = departureHours < 6 ? departureTimeInMinutes + (24 * 60) : departureTimeInMinutes
        const adjustedCurrentTime = currentTimeInMinutes

        return adjustedDepartureTime > adjustedCurrentTime
      })
    }
  }
}
</script>

<style scoped>
/* Hide scrollbar while maintaining scroll functionality */
.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
